import type { ApiClient, QueryValue } from '@platform/api-client';
import type {
  ApiPaths,
  OperationIn,
  PathParams,
  PathsIn,
  QueryParams,
  RequestBody,
  ResponseBody,
  ShippingPaths,
} from './operations.ts';

type Absent<K extends string> = Partial<Record<K, never>>;

type Requires<T, K extends string> = [T] extends [never] ? Absent<K> : Readonly<Record<K, T>>;

type Allows<T, K extends string> = [T] extends [never] ? Absent<K> : Readonly<Partial<Record<K, T>>>;

export type TypedRequest<O> = Requires<PathParams<O>, 'path'> &
  Requires<RequestBody<O>, 'body'> &
  Allows<QueryParams<O>, 'query'> & { readonly idempotencyKey?: string };

type Method = 'get' | 'post' | 'patch' | 'put' | 'delete';

export type TypedClient<Paths = ApiPaths> = {
  readonly [M in Method]: <P extends PathsIn<Paths, M> & keyof Paths>(
    path: P,
    request: TypedRequest<OperationIn<Paths, P, M>>,
  ) => Promise<ResponseBody<OperationIn<Paths, P, M>>>;
};

export type ShippingClient = TypedClient<ShippingPaths>;

type LooseRequest = {
  readonly path?: Record<string, string>;
  readonly query?: Record<string, unknown>;
  readonly body?: unknown;
  readonly idempotencyKey?: string;
};

const PARAM = /\{(?<name>[A-Za-z_][A-Za-z0-9_]*)\}/gu;

export const expandPath = (template: string, params: Record<string, string> = {}): string =>
  template.replace(PARAM, (_match, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`missing path parameter ${name}`);
    return encodeURIComponent(value);
  });

type QueryParameter = QueryValue | readonly QueryValue[];

const scalar = (value: unknown): QueryValue =>
  typeof value === 'object' ? JSON.stringify(value) : (value as QueryValue);

const toQueryParameter = (value: unknown): QueryParameter =>
  Array.isArray(value) ? value.map(scalar) : scalar(value);

const toQuery = (query: Record<string, unknown> = {}): Record<string, QueryParameter> =>
  Object.fromEntries(
    Object.entries(query).flatMap(([key, value]) =>
      value === undefined || value === null ? [] : [[key, toQueryParameter(value)] as const],
    ),
  );

const send = (client: ApiClient, method: string, path: string, request: LooseRequest): Promise<unknown> =>
  client.request(expandPath(path, request.path).replace(/^\//u, ''), {
    method: method.toUpperCase(),
    query: toQuery(request.query),
    ...(request.body === undefined ? {} : { body: request.body }),
    ...(request.idempotencyKey === undefined ? {} : { idempotencyKey: request.idempotencyKey }),
  });

const method =
  (client: ApiClient, verb: Method) =>
  (path: string, request: LooseRequest): Promise<unknown> =>
    send(client, verb, path, request);

export const createTypedClient = <Paths = ApiPaths>(client: ApiClient): TypedClient<Paths> =>
  ({
    get: method(client, 'get'),
    post: method(client, 'post'),
    patch: method(client, 'patch'),
    put: method(client, 'put'),
    delete: method(client, 'delete'),
  }) as TypedClient<Paths>;
